import { isApiPlanId, type ApiPlanId } from "./apiPlanCarry";

/** Monthly call cap per paid API plan. One source for the Stripe webhook and for the welcome-key claim below. */
export const API_PLAN_CALL_LIMITS: Record<ApiPlanId, number> = {
  premium: 200_000,
  ultra: 750_000,
  mega: 2_000_000,
};

/** The few Stripe Checkout Session fields the rules need (a Stripe.Checkout.Session satisfies this). */
export interface WelcomeSessionFacts {
  client_reference_id?: string | null;
  status?: string | null;
  payment_status?: string | null;
  metadata?: Record<string, string> | null;
  /** Status of the subscription the session created (expanded), if known. */
  subscriptionStatus?: string | null;
  subscriptionId?: string | null;
  customerId?: string | null;
}

export type WelcomeVerdict =
  | { ok: true; plan: ApiPlanId; subscriptionId: string | null; customerId: string | null }
  | { ok: false; code: "FORBIDDEN" | "PRECONDITION_FAILED"; message: string };

/**
 * Who may be handed a paid API key after Checkout. Only the signed-in user the session was created for (client_reference_id),
 * for an API-plan session (metadata.type "api" with a real plan), that is complete and paid (or free by promo code), and whose
 * subscription is live (active or trialing). Anything else gets no key, so a plan-less user can never obtain a paid one.
 */
export function judgeWelcomeSession(userId: number, s: WelcomeSessionFacts): WelcomeVerdict {
  if (!userId || s.client_reference_id !== String(userId)) {
    return { ok: false, code: "FORBIDDEN", message: "This checkout belongs to a different account." };
  }
  const plan = s.metadata?.plan;
  if (s.metadata?.type !== "api" || !isApiPlanId(plan)) {
    return { ok: false, code: "FORBIDDEN", message: "This checkout is not an API plan." };
  }
  if (s.status !== "complete" || (s.payment_status !== "paid" && s.payment_status !== "no_payment_required")) {
    return { ok: false, code: "PRECONDITION_FAILED", message: "Payment is not complete yet." };
  }
  if (s.subscriptionStatus !== "active" && s.subscriptionStatus !== "trialing") {
    return { ok: false, code: "PRECONDITION_FAILED", message: "The subscription is not active." };
  }
  return { ok: true, plan, subscriptionId: s.subscriptionId ?? null, customerId: s.customerId ?? null };
}

export interface SubscriptionFacts {
  id: string;
  status: string;
  /** Price ids on the subscription's items. */
  priceIds: string[];
}

/**
 * The API plan a user is entitled to from their own Stripe subscriptions (the later-visit claim, when no session id is at hand).
 * Only subscriptions that are active or trialing and carry one of the configured API plan prices count; the highest plan wins.
 * Returns null when there is none, so a user with no live API subscription can never get a paid key.
 */
export function pickApiSubscription(
  subs: SubscriptionFacts[],
  priceIdByPlan: Partial<Record<ApiPlanId, string | undefined>>,
): { plan: ApiPlanId; subscriptionId: string } | null {
  const rank: ApiPlanId[] = ["mega", "ultra", "premium"];
  let best: { plan: ApiPlanId; subscriptionId: string } | null = null;
  for (const sub of subs) {
    if (sub.status !== "active" && sub.status !== "trialing") continue;
    for (const plan of rank) {
      const priceId = priceIdByPlan[plan];
      if (!priceId || !sub.priceIds.includes(priceId)) continue;
      if (!best || rank.indexOf(plan) < rank.indexOf(best.plan)) best = { plan, subscriptionId: sub.id };
    }
  }
  return best;
}
